import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, MapPin, Phone } from "lucide-react";
import logo from "@/assets/live-stay-logo.png";
import { DevelopmentService } from "@/services/developmentService";
import { developmentHref } from "@/lib/developmentLinks";

type FooterLink = {
  name: string;
  path: string;
};

const Footer = () => {
  const [developmentLinks, setDevelopmentLinks] = useState<FooterLink[]>([
    { name: "Porto 2 Life", path: "/resorts/porto-2-life" },
    { name: "Exclusive Gramado", path: "/resorts/exclusive-gramado" },
  ]);

  useEffect(() => {
    let mounted = true;

    const loadDevelopments = async () => {
      try {
        const developments = await DevelopmentService.getAllDevelopments();
        if (!mounted || developments.length === 0) return;

        const active = developments.filter((d) => d.is_active !== false);
        const links = active.map((development) => ({
          name: development.name,
          path: developmentHref(development),
        }));
        setDevelopmentLinks(links);
      } catch {
        // Mantém links padrão quando a API estiver indisponível.
      }
    };

    loadDevelopments();

    return () => {
      mounted = false;
    };
  }, []);

  // Acessos unificado: junta os botões originais do menu de acessos
  // com os links de suporte/legal numa única lista visível.
  const accessLinks: FooterLink[] = [
    { name: "Vouchers", path: "/vouchers" },
    { name: "Parceiros", path: "/parceiros" },
    { name: "Sobre", path: "/sobre" },
    { name: "Fale Conosco", path: "/fale-conosco" },
    { name: "Ajuda", path: "/ajuda" },
    { name: "Política de Uso", path: "/politica" },
    { name: "Privacidade", path: "/privacidade" },
    { name: "Termos", path: "/termos" },
    { name: "Mapa do site", path: "/sitemap" },
  ];

  return (
    <footer id="contato" className="bg-foreground text-background mt-10">
      <div className="container mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <div className="grid md:grid-cols-5 gap-10">
          {/* Brand column - 2 cols on desktop */}
          <div className="space-y-5 md:col-span-2">
            <Link to="/" aria-label="LiveStay" className="inline-flex items-center">
              <span className="inline-flex items-center rounded-xl bg-white px-4 py-3 shadow-sm">
                <img src={logo} alt="LiveStay" className="h-12 w-auto object-contain" />
              </span>
            </Link>

            <p className="text-sm leading-relaxed opacity-80 max-w-md">
              Conectamos você aos melhores resorts do Brasil. Experiências autênticas,
              conforto e atendimento exclusivo em cada estadia.
            </p>
          </div>

          {/* Contato */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-[0.18em] opacity-90">
              Contato
            </h4>
            <ul className="space-y-3 text-sm opacity-85">
              <li className="flex items-start gap-2">
                <MapPin className="size-4 mt-0.5 shrink-0 opacity-80" aria-hidden="true" />
                <span>Brasil — Atendimento online</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="size-4 shrink-0 opacity-80" aria-hidden="true" />
                <a
                  href="mailto:livestaybrasil@gmail.com"
                  className="hover:opacity-100 transition-opacity"
                >
                  livestaybrasil@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="size-4 shrink-0 opacity-80" aria-hidden="true" />
                <a
                  href="https://wa.me/5500000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-100 transition-opacity"
                >
                  WhatsApp da Central
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Instagram className="size-4 shrink-0 opacity-80" aria-hidden="true" />
                <a
                  href="https://www.instagram.com/livestaybrasil/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-100 transition-opacity"
                >
                  @livestaybrasil
                </a>
              </li>
            </ul>
          </div>

          {/* Empreendimentos ativos */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-[0.18em] opacity-90">
              Empreendimentos
            </h4>
            <ul className="space-y-2 text-sm opacity-85">
              {developmentLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="hover:opacity-100 transition-opacity"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Acessos */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-[0.18em] opacity-90">
              Acessos
            </h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm opacity-85">
              {accessLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="hover:opacity-100 transition-opacity"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Idioma / moeda */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3 text-xs opacity-75">
          <span className="rounded-full bg-white/10 px-3 py-1">Português (BR)</span>
          <span className="rounded-full bg-white/10 px-3 py-1">R$ BRL</span>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 py-5 text-xs opacity-70 flex flex-wrap justify-between gap-2">
          <span>
            © {new Date().getFullYear()} LiveStay. Todos os direitos reservados.
          </span>
          <span>Feito com <span aria-hidden="true">♥</span> para hóspedes exigentes</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
